// ============================================================================
// Reto de seguridad — el diálogo que pide un código, abierto desde cualquier
// parte de la app.
// ============================================================================
// Lo abren tres caminos, todos con `requestSecurityChallenge()`:
//   • el interceptor de la API, cuando una operación de riesgo responde 403
//     `STEP_UP_REQUIRED` (y luego repite la petición original);
//   • el interceptor, cuando la sesión tiene el reto de inicio pendiente (403
//     `TWO_FACTOR_CHALLENGE_REQUIRED`, p. ej. tras recargar la página);
//   • el inicio de sesión, cuando `/login` responde `twoFactorRequired`.
//
// Una pantalla puede disparar varias peticiones a la vez y todas chocar con el
// mismo 403: mientras el diálogo está abierto comparten UNA promesa, así que la
// persona escribe el código una sola vez y todas se repiten.
//
// Sin dependencias de `utils/api` a propósito: el interceptor importa este
// módulo, y un ciclo api → store → api dejaría a uno de los dos a medio cargar.
// ============================================================================

import { create } from "zustand";

export type ChallengePurpose = "login" | "step_up";

export interface ChallengeRequest {
  purpose: ChallengePurpose;
  /** Llave de la operación de riesgo (viene en el 403), para decir qué se confirma. */
  operation?: string;
  /** Nombre de esa operación, tal como lo manda el backend. */
  label?: { es?: string; en?: string };
}

interface ChallengeState {
  request: ChallengeRequest | null;
  open: (request: ChallengeRequest) => void;
  close: () => void;
}

export const useSecurityChallengeStore = create<ChallengeState>((set) => ({
  request: null,
  open: (request) => set({ request }),
  close: () => set({ request: null }),
}));

let pending: Promise<boolean> | null = null;
let settle: ((ok: boolean) => void) | null = null;

/**
 * Abre el diálogo y espera. `true` = código correcto; `false` = lo cerró sin
 * darlo. Si ya hay uno abierto, se suma a ése.
 */
export function requestSecurityChallenge(request: ChallengeRequest): Promise<boolean> {
  if (pending) return pending;
  pending = new Promise<boolean>((resolve) => {
    settle = resolve;
  });
  useSecurityChallengeStore.getState().open(request);
  return pending;
}

/** Lo llama el diálogo al terminar (bien o cancelado). */
export function resolveSecurityChallenge(ok: boolean): void {
  const done = settle;
  pending = null;
  settle = null;
  useSecurityChallengeStore.getState().close();
  done?.(ok);
}

/** ¿Hay un reto abierto ahora mismo? */
export const isSecurityChallengeOpen = (): boolean => pending !== null;
